import React, { useMemo } from 'react';
import type { ContactInput } from 'sr-types/lib/person/v1/graphql';
import RoundAvatar, { AvatarSizes } from '../../../production/header/RoundAvatar';
import type { EntityName } from '../../auth/Security';
import { SECURITY_ACTIONS } from '../../auth/Security';
import { getNameLabel } from '../utils/autocompleteLabelUtils';
import I18n, { useI18n } from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import { H, V } from '../../Layout';
import { InfoContainer } from '../../list/dataListComponents/InfoContainer';
import { useStyling } from '../../Theme';
import type { SecuredActionType } from '../ActionType';
import Confirm from '../Confirm';
import ResponsiveActions from '../ResponsiveActions';

type ContactRendererProps = {
    contact: ContactInputWithProfileImage;
    contactActionId?: string;
    setCurrentPerson: (id: string | null) => void;
    onEdit: (contact: ContactInput) => void;
    onDeleteClicked: (contact: ContactInput) => void;
    isDeleteConfirmationOpen: boolean;
    handleDeleteConfirmed: () => void;
    removingBusinessContact: boolean;
    setIsDeleteConfirmationOpen: (open: boolean) => void;
    currentPerson: string | null;
    hideBottomBorder?: boolean;
    /** Parent entity that owns the contact mutation (person business contacts vs shortlist representative). */
    entityName: EntityName;
};

type ContactInputWithProfileImage = ContactInput & {
    profileImage?: string;
    profileImageReference?: {
        id?: string;
    };
};

type ContactPreviewItem = Omit<ContactInputWithProfileImage, 'name'> & {
    name: string;
};

const CONTACT_NAME_SX = {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.25,
    color: 'text.primary'
};
const CONTACT_PREVIEW_ICON_SX = { fontSize: '12px' };

const getContactDisplayName = (contact: ContactInputWithProfileImage): string => {
    return getNameLabel(contact?.name).trim() || contact?.personReference?.label || '';
};

export default function ContactRenderer({
    contact,
    contactActionId = undefined,
    setCurrentPerson,
    onEdit,
    onDeleteClicked,
    isDeleteConfirmationOpen,
    handleDeleteConfirmed,
    removingBusinessContact,
    setIsDeleteConfirmationOpen,
    currentPerson,
    hideBottomBorder = false,
    entityName
}: ContactRendererProps) {
    const { theme } = useStyling();
    const t = useI18n();

    const securedActions = useMemo<SecuredActionType[]>(
        () => [
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName,
                label: t('actions.edit'),
                icon: Icons.Edit,
                onClick: () => onEdit(contact)
            },
            {
                securityAction: SECURITY_ACTIONS.DELETE,
                entityName,
                label: t('actions.delete'),
                icon: Icons.Delete,
                onClick: () => onDeleteClicked(contact)
            }
        ],
        [contact, entityName, onDeleteClicked, onEdit, t]
    );

    const contactId = contactActionId || contact?.personReference?.id;

    if (!contactId) {
        return null;
    }

    const organizationName = contact.organizationReference?.label || contact.organization;
    const contactName = getContactDisplayName(contact);
    const profileImageId = contact.profileImage?.split('/').pop() || contact.profileImageReference?.id;
    const contactPreviewItem: ContactPreviewItem = {
        ...contact,
        name: contactName
    };

    return (
        <V
            onMouseEnter={() => setCurrentPerson(contactId)}
            onMouseLeave={() => setCurrentPerson(null)}
            sx={{
                position: 'relative',
                borderBottom: hideBottomBorder ? 'none' : `1px solid ${theme.palette.divider}`
            }}
        >
            <H gap={2} sx={{ position: 'relative', alignItems: 'center' }}>
                <RoundAvatar title={contactName} imageId={profileImageId} size={AvatarSizes.md} />
                <InfoContainer
                    item={contactPreviewItem}
                    editEnabled={false}
                    previewOrganizationName={organizationName}
                    nameSx={CONTACT_NAME_SX}
                    previewContactIconSx={CONTACT_PREVIEW_ICON_SX}
                />
                <ResponsiveActions
                    isActivated={currentPerson === contactId}
                    securedActions={securedActions}
                    showBorder
                    iconSx={{ color: 'action.active' }}
                />
            </H>
            <Confirm
                open={isDeleteConfirmationOpen}
                title={<I18n token="delete.confirm.title" />}
                body={<I18n token="delete.confirm.message" />}
                onConfirm={handleDeleteConfirmed}
                onCancel={() => setIsDeleteConfirmationOpen(false)}
                isLoading={removingBusinessContact}
            />
        </V>
    );
}
